import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { User } from '@ml/core/types/classes';
import { map, tap } from 'rxjs/operators';
import { UserRole } from '@ml/core/types/enums';
import { ConnectionService } from '@ml/core/services/connection.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly url = `users`;
  user$: BehaviorSubject<User> = new BehaviorSubject(null);

  constructor(
    private http: HttpClient,
    private connectionService: ConnectionService
  ) {}

  get() {
    if (this.user$.value) {
      return this.user$.asObservable();
    }

    if (
      !this.connectionService.connection &&
      localStorage.getItem('currentUser')
    ) {
      this.user$.next(JSON.parse(localStorage.getItem('currentUser')));
      return this.user$.asObservable();
    }

    return this.http.get('user').pipe(
      tap(user => localStorage.setItem('currentUser', JSON.stringify(user))),
      tap((user: User) => this.user$.next(user))
    );
  }

  changeOwnPassword(password: string) {
    return this.http
      .patch(`user/change-password`, {
        password
      })
      .pipe(
        tap(() => {
          this.user$.next({
            ...this.user$.value,
            passwordChanged: new Date().toISOString()
          });
        })
      );
  }

  changeOthersPassword(id: number, password: string) {
    return this.http.patch(`users/${id}/change-password`, {
      password
    });
  }

  setFCMToken(fcmToken: string) {
    return this.http
      .patch<User>(`${this.url}/${this.user.id}`, { fcmToken })
      .pipe(
        tap(_user_ => {
          this.user.fcmToken = fcmToken;
          this.user$.next({ ...this.user });
        })
      );
  }

  get user() {
    return this.user$.value;
  }

  get isEmployee() {
    return this.user && this.user.role === UserRole.EMPLOYEE;
  }

  get isAdmin() {
    return this.user && this.user.role === UserRole.ADMIN;
  }

  removeCurrentUser() {
    this.user$.next(null);
    localStorage.removeItem('currentUser');
  }
}
